import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Breadcrumbs,
  Grid,
  Chip,
  Divider,
  alpha,
  useTheme
} from '@mui/material';
import {
  LibraryBooks as LibraryBooksIcon,
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import { Link, useParams, useNavigate } from 'react-router-dom';

// Sample accounts data (same as in GeneralLedger)
const sampleAccounts = [
  { id: '1000', name: 'الأصول', accountNumber: '1000' },
  { id: '1100', name: 'الأصول المتداولة', accountNumber: '1100' },
  { id: '1110', name: 'النقدية', accountNumber: '1110' },
  { id: '1120', name: 'البنك الأهلي', accountNumber: '1120' },
  { id: '2000', name: 'الخصوم', accountNumber: '2000' },
  { id: '3000', name: 'الإيرادات', accountNumber: '3000' },
  { id: '3100', name: 'إيرادات المبيعات', accountNumber: '3100' },
  { id: '4000', name: 'المصروفات', accountNumber: '4000' },
  { id: '4100', name: 'مصاريف الإيجار', accountNumber: '4100' },
  { id: '4200', name: 'مصاريف الرواتب', accountNumber: '4200' },
];

// Sample journal entries (same as in GeneralLedger)
const sampleEntries = [
  {
    id: 'JE10001',
    date: '2025-03-10',
    reference: 'INV-1001',
    description: 'تسجيل إيرادات مبيعات',
    status: 'مرحل',
    totalAmount: 5000,
    createdBy: 'محمد أحمد',
    createdAt: '2025-03-10T10:30:00',
    lines: [
      { id: 1, accountId: '1110', accountName: 'النقدية', debit: 5000, credit: 0, description: 'تحصيل نقدي' },
      { id: 2, accountId: '3100', accountName: 'إيرادات المبيعات', debit: 0, credit: 5000, description: 'مبيعات منتج أ' },
    ]
  },
  {
    id: 'JE10002',
    date: '2025-03-12',
    reference: 'EXP-2001',
    description: 'دفع إيجار المكتب',
    status: 'مرحل',
    totalAmount: 7500,
    createdBy: 'أحمد خالد',
    createdAt: '2025-03-12T14:15:00',
    lines: [
      { id: 1, accountId: '4100', accountName: 'مصاريف الإيجار', debit: 7500, credit: 0, description: 'إيجار شهر مارس' },
      { id: 2, accountId: '1120', accountName: 'البنك الأهلي', debit: 0, credit: 7500, description: 'دفع من الحساب البنكي' },
    ]
  },
  {
    id: 'JE10003',
    date: '2025-03-15',
    reference: 'SAL-3001',
    description: 'دفع رواتب الموظفين',
    status: 'مرحل',
    totalAmount: 12000,
    createdBy: 'فهد سعيد',
    createdAt: '2025-03-15T16:45:00',
    lines: [
      { id: 1, accountId: '4200', accountName: 'مصاريف الرواتب', debit: 12000, credit: 0, description: 'رواتب شهر مارس' },
      { id: 2, accountId: '1120', accountName: 'البنك الأهلي', debit: 0, credit: 12000, description: 'دفع من الحساب البنكي' },
    ]
  }
];

// نوع البيانات لسطر القيد المحاسبي
interface JournalEntryLine {
  id: number;
  accountId: string;
  accountName: string;
  debit: number;
  credit: number;
  description: string;
}

// نوع البيانات للقيد المحاسبي
interface JournalEntry {
  id: string;
  date: string;
  reference: string;
  description: string;
  status: string;
  totalAmount: number;
  createdBy: string;
  createdAt: string;
  lines: JournalEntryLine[];
}

const JournalEntryView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [entry, setEntry] = useState<JournalEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // محاكاة جلب بيانات القيد
  useEffect(() => {
    setLoading(true);
    
    // محاكاة تأخير الطلب API
    const timer = setTimeout(() => {
      const foundEntry = sampleEntries.find(e => e.id === id);
      
      if (foundEntry) {
        setEntry(foundEntry);
        setError(null);
      } else {
        setEntry(null);
        setError('القيد المحاسبي غير موجود');
      }
      
      setLoading(false);
    }, 800);
    
    return () => clearTimeout(timer);
  }, [id]);
  
  // عرض حالة القيد
  const renderEntryStatus = (status: string) => {
    let color: 'success' | 'warning' | 'default' = 'default';
    
    switch (status) {
      case 'مرحل':
        color = 'success';
        break;
      case 'غير مرحل':
        color = 'warning';
        break;
      default:
        color = 'default';
    }
    
    return (
      <Chip 
        size="small" 
        label={status} 
        color={color}
        sx={{ 
          fontWeight: 500,
          fontSize: '0.75rem'
        }}
      />
    );
  };
  
  // طباعة القيد المحاسبي
  const handlePrint = () => {
    window.print();
  };
  
  // عرض القيد للتعديل
  const handleEdit = () => {
    if (entry) {
      navigate(`/finance/ledger/edit/${entry.id}`);
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={40} sx={{ color: '#30c3b0' }} />
      </Box>
    );
  }
  
  if (error) {
    return (
      <Box sx={{ 
        py: 8, 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        opacity: 0.8
      }}>
        <Typography variant="h6" color="error">
          {error}
        </Typography>
        <Button
          variant="contained"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/finance/ledger')}
          sx={{
            mt: 2,
            bgcolor: '#30c3b0',
            '&:hover': { bgcolor: '#25a899' },
          }}
        >
          العودة لدفتر الأستاذ
        </Button>
      </Box>
    );
  }
  
  if (!entry) {
    return null;
  }
  
  return (
    <Box sx={{ mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 2,
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          border: '1px solid',
          borderColor: alpha('#000', 0.08),
          background: 'white'
        }}
      >
        {/* العنوان ومسار التنقل */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', mb: 1 }}>
              <LibraryBooksIcon sx={{ mr: 1, color: '#30c3b0' }} />
              عرض القيد المحاسبي {entry.id}
            </Typography>
            
            <Breadcrumbs separator="›" aria-label="breadcrumb">
              <Link to="/finance" style={{ textDecoration: 'none', color: theme.palette.primary.main }}>
                الحسابات المالية
              </Link>
              <Link to="/finance/ledger" style={{ textDecoration: 'none', color: theme.palette.primary.main }}>
                دفتر الأستاذ العام
              </Link>
              <Typography color="text.primary">عرض القيد {entry.id}</Typography>
            </Breadcrumbs>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              component={Link}
              to="/finance/ledger"
              sx={{
                borderColor: '#30c3b0',
                color: '#30c3b0',
                '&:hover': { borderColor: '#25a899', backgroundColor: alpha('#30c3b0', 0.04) },
              }}
            >
              العودة للدفتر
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              sx={{
                borderColor: theme.palette.info.main,
                color: theme.palette.info.main,
                '&:hover': { borderColor: theme.palette.info.dark, backgroundColor: alpha(theme.palette.info.main, 0.04) },
              }}
            >
              طباعة
            </Button>
            
            {entry.status !== 'مرحل' && (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleEdit}
                sx={{
                  bgcolor: '#30c3b0',
                  '&:hover': { bgcolor: '#25a899' },
                }}
              >
                تعديل
              </Button>
            )}
          </Box>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        <Box className="print-content">
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">رقم القيد</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>{entry.id}</Typography>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">التاريخ</Typography>
              <Typography variant="body1">{entry.date}</Typography>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">رقم المرجع</Typography>
              <Typography variant="body1">{entry.reference || '-'}</Typography>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">الحالة</Typography>
              <Box>{renderEntryStatus(entry.status)}</Box>
            </Grid>
          </Grid>
          
          <Box sx={{ mb: 4 }}>
            <Typography variant="body2" color="text.secondary">الوصف</Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>{entry.description}</Typography>
          </Box>
          
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            تفاصيل القيد
          </Typography>
          
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), '& th': { fontWeight: 600 } }}>
                  <TableCell width="40%">الحساب</TableCell>
                  <TableCell>البيان</TableCell>
                  <TableCell align="right" width="15%">مدين</TableCell>
                  <TableCell align="right" width="15%">دائن</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entry.lines.map((line) => (
                  <TableRow key={line.id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{line.accountName}</TableCell>
                    <TableCell>{line.description}</TableCell>
                    <TableCell align="right">
                      {line.debit > 0 ? `${line.debit.toLocaleString()} ر.س` : ''}
                    </TableCell>
                    <TableCell align="right">
                      {line.credit > 0 ? `${line.credit.toLocaleString()} ر.س` : ''}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                  <TableCell colSpan={2} sx={{ fontWeight: 600 }}>المجموع</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {entry.totalAmount.toLocaleString()} ر.س
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {entry.totalAmount.toLocaleString()} ر.س
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">أنشئ بواسطة</Typography>
              <Typography variant="body1">{entry.createdBy}</Typography>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">تاريخ الإنشاء</Typography>
              <Typography variant="body1">
                {new Date(entry.createdAt).toLocaleString('ar-SA')}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default JournalEntryView;